import { compact, head } from 'lodash/fp';
import type { NavigateFn } from '@tanstack/react-router';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import {
    createPrivilegesMap,
    GeneralEnterprisePrivileges,
    GeneralPlatformPrivileges,
    getCurrentEnterpriseId,
    getStrictCurrentEnterpriseId,
    isCurrentSessionLoggedIn,
    setCurrentSession,
    setCurrentUser,
    setPlatformPrivileges,
    setPrivileges,
} from '@mews-commander/core';
import {
    GeneralApplicationScope,
    GeneralApplicationScopeType as ScopeType,
    GeneralArea,
    GeneralAuthenticatorType,
    GeneralDeviceSystem,
    GeneralPasswordStrength,
    GeneralSignInMethod,
    GeneralUserAccessResult,
} from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { CommanderThunkAction } from '@mews-commander/types';
import { removeCookie } from '@mews-commander/utils';
import { createAction, getBrowserFingerprint, setSentryTags } from '@mews/framework';
import { fetchResources } from '@mews-commander/legacy-entities/src/actions/resources';
import { fetchAllBookableServices } from '@mews-commander/legacy-entities/src/actions/services';
import { USER_CHAINS_LOADED } from '@mews-commander/legacy-entities/src/ducks/chains';
import { getNativeLanguageCodeByCountryCode } from '@mews-commander/legacy-entities/src/ducks/countries';
import { fetchEnterpriseIntegrations } from '@mews-commander/legacy-entities/src/ducks/enterpriseIntegrations';
import { setEnterpriseRelationships } from '@mews-commander/legacy-entities/src/ducks/enterpriseRelationships';
import {
    doesEnterpriseExist as doesEnterpriseExistSelector,
    enterprisesLoaded,
    getEnterpriseDefaultCultureName,
    getEnterpriseDefaultLanguageCode,
} from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { SCOPES_LOADED } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { SUBSCRIPTIONS_LOADED } from '@mews-commander/legacy-entities/src/ducks/subscriptions';
import { initSalesForce } from '@mews-commander/chat';
import { setFeatureFlagServiceUser } from '../../app/reducers/featureFlags';
import { fetchCultureInfos, fetchLocalizedTexts } from '../../app/actions/localizationData';
import { setGainsightUser } from '../../app/actions/gainsight';
import { setFullviewUser } from '../../app/actions/fullview';
import { loadBeamer } from '../../app/actions/beamerActions';
import { connectToWebsocket } from '../../../core/websockets/actions';
import { isFunctionalConsentGiven } from '../../trackingConsent/selectors';
import { setPasswordStrength, setSuggestPasskeySetup } from '../screens/usePublicStore';
import { EnterpriseIdStorageService } from './enterpriseIdStorageService';

export const GENERAL_SIGN_IN_PARAMS = {
    setCookie: true,
    extent: {
        scopes: true,
        chains: true,
        enterprises: true,
        subscriptions: true,
        customLinks: true,
        enterpriseRelationships: true,
        portfolioProductSubscriptions: true,
    },
};

/* Reduced extent on initial sign-in */
export const INITIAL_SIGN_IN_PARAMS = {
    setCookie: true,
    extent: {
        scopes: true,
    },
};

export const DEVICE_AUTH_REQUIRED = 'Device authorization required';

export const checkForForced2FA =
    ({
        authenticationInfo,
        ...other
    }: GeneralUserAccessResult): CommanderThunkAction<Promise<GeneralUserAccessResult>> =>
    async (dispatch, getState, { navigate }) => {
        if (
            authenticationInfo !== null &&
            authenticationInfo?.authenticator === GeneralAuthenticatorType.None &&
            authenticationInfo?.setupEnforced === true
        ) {
            void navigate({ to: '/Home/Configure2FA/Index' });
            return Promise.reject();
        }

        return Promise.resolve({ authenticationInfo, ...other });
    };

export const checkForHardDeviceApproval =
    ({
        userDeviceAuthorizationInfo,
        ...other
    }: GeneralUserAccessResult): CommanderThunkAction<Promise<GeneralUserAccessResult>> =>
    async (dispatch, getState, { navigate }) => {
        if (userDeviceAuthorizationInfo?.authorizationRequired) {
            await navigate({ to: '/Home/NewDevice' });
            return Promise.reject(new Error(DEVICE_AUTH_REQUIRED));
        }

        return Promise.resolve({ userDeviceAuthorizationInfo, ...other });
    };

export const checkForConsent =
    ({ consent, ...other }: GeneralUserAccessResult): CommanderThunkAction<Promise<GeneralUserAccessResult>> =>
    async (dispatch, getState, { navigate }) => {
        if (consent) {
            void navigate({ to: '/Home/Consent' });
            return Promise.reject();
        }
        return Promise.resolve({ consent, ...other });
    };

function isPlatformAdmin(scope: GeneralApplicationScope) {
    // @ts-expect-error $TSFixMeError: Property accessAllEnterprises does not exist on type {}
    return scope.type === ScopeType.Platform && scope.privileges?.accessAllEnterprises === true;
}

export const initializeUserSession =
    ({
        accessInfo,
        passkeyInfo,
        ...rest
    }: GeneralUserAccessResult): CommanderThunkAction<Promise<GeneralUserAccessResult>> =>
    async (dispatch, getState, { featureFlagService, repositories, navigate }) => {
        if (!accessInfo) {
            throw new Error('initializeUserSession requires accessInfo');
        }
        const { scopes } = accessInfo;
        dispatch(createAction(SCOPES_LOADED, scopes));

        const state = getState();

        const { currentEnterpriseId, currentProfileId, currentScope } = await getEnterpriseIdProfileIdAndScope({
            scopes,
            doesEnterpriseExist: (enterpriseId) => doesEnterpriseExistSelector(state, { enterpriseId }),
            navigate,
        });

        setUserPrivileges({ currentEnterpriseId, currentScope });

        // This keeps the last opened enterprise stored to be used as default in new tabs for users' convenience
        EnterpriseIdStorageService.set(currentEnterpriseId);

        /**
         * Currently the user's languageCode and cultureCode returned from the SignIn endpoint is dependent on a matched profile.
         * The profile is matched based on provided ProfileId parameter, either directly or from a cookie. However,
         * during an initial sign in, this ProfileId is unknown, thus the user returned is based on some default fallback.
         * But when you refresh the page while being signed or when you do subsequent requests, different profile is used.
         *
         * This leads to hard to spot error for users with platform profiles, because Frontend culture and Backend culture will
         * mismatch, especially in Backend templates.
         *
         * A quick fix is to call the endpoint one more time to get proper values. Proper fix is to separate the SignIn endpoint
         * into two and sequence them properly.
         *
         * See https://mews.myjetbrains.com/youtrack/issue/RND-179408/General-API-users-signIn-returns-incorrect-user-values-on-a-first-call
         */
        const result = await repositories.generalUsers.signIn({
            method: GeneralSignInMethod.AccessToken,
            device: getDeviceFingerprintPayload(),
            application: GeneralArea.Commander,
            extent: GENERAL_SIGN_IN_PARAMS.extent,
            // @ts-expect-error ProfileId is generated as a common parameter in OpenAPI SDK and thus can't be passed in directly
            profileId: currentProfileId,
        });
        if (!result.accessInfo) {
            throw new Error('Failed to load user');
        }
        const {
            user,
            isSecure,
            accessToken,
            chains,
            enterprises,
            subscriptions,
            customLinks,
            enterpriseRelationships,
        } = result.accessInfo;

        dispatch(createAction(USER_CHAINS_LOADED, chains));
        dispatch(enterprisesLoaded({ enterprises, customLinks: customLinks || [] }));
        dispatch(createAction(SUBSCRIPTIONS_LOADED, subscriptions));
        dispatch(setEnterpriseRelationships(enterpriseRelationships || []));

        setCurrentUser({ isSecure, ...user });

        const { languageCode, cultureCode, nationalityCode } = user;

        setCurrentSession({
            currentProfileId: currentProfileId as string,
            currentEnterpriseId: currentEnterpriseId as string,
            accessToken: accessToken as string,
            languageCode:
                languageCode ||
                getNativeLanguageCodeByCountryCode(state, { countryCode: nationalityCode }) ||
                getEnterpriseDefaultLanguageCode(state, { enterpriseId: currentEnterpriseId as string }),
            cultureCode:
                cultureCode || getEnterpriseDefaultCultureName(state, { enterpriseId: currentEnterpriseId as string }),
        });

        if (featureFlagService.hasFlag(FeatureFlag.passwordScannerEnabled)) {
            setPasswordStrength(result.passwordStrength || null);
        }

        if (featureFlagService.hasFlag(FeatureFlag.passkeySignInUiEnabled)) {
            // passkeyInfo will be true only in the first sign-in response sent
            setSuggestPasskeySetup(passkeyInfo?.suggestSetup || false);
        }

        if (isFunctionalConsentGiven(state) && !featureFlagService.hasFlag(FeatureFlag.adaEmbeddedFeatureFlagEnabled)) {
            dispatch(initSalesForce());
        }

        dispatch(loadBeamer(user));

        dispatch(connectToWebsocket());

        if (currentEnterpriseId) {
            void dispatch(fetchEnterpriseIntegrations({ enterpriseId: currentEnterpriseId }));
            void dispatch(setSentryTags({ enterpriseId: currentEnterpriseId }));
        }

        await Promise.all(
            compact([
                dispatch(setFeatureFlagServiceUser({ isAuthorized: !!accessToken, currentUser: user })),
                dispatch(fetchCultureInfos()),
                dispatch(fetchLocalizedTexts()),
                currentEnterpriseId &&
                    dispatch(
                        fetchAllBookableServices({
                            enterpriseId: currentEnterpriseId,
                            enterpriseIds: [currentEnterpriseId],
                            extent: {
                                services: true,
                                registrationCardConfigurations: true,
                                ageCategories: true,
                            },
                        })
                    ),
                currentEnterpriseId && dispatch(fetchResources()),
                dispatch(setGainsightUser(currentProfileId as string)),
                dispatch(setFullviewUser()),
            ])
        );

        return { accessInfo, passkeyInfo, ...rest };
    };

const getEnterpriseIdProfileIdAndScope = async ({
    scopes,
    doesEnterpriseExist,
    navigate,
}: {
    scopes: GeneralApplicationScope[];
    doesEnterpriseExist: (enterpriseId: string) => boolean;
    navigate: NavigateFn;
}) => {
    let currentEnterpriseId: string | undefined | null;
    let currentProfileId: string | undefined | null;
    /**
     * If the value is present, it was parsed from the URL.
     * Otherwise we will have to figure out a reasonable default value.
     */
    currentEnterpriseId = getCurrentEnterpriseId();

    let currentScope: GeneralApplicationScope | undefined;
    const commanderScopes = scopes.filter((s) => s.type !== ScopeType.Personal);
    /**
     * If no `enterpriseId` was matched in url, we find some default
     */
    if (currentEnterpriseId === undefined) {
        /**
         * For users' convenience, we use local storage to remember last opened enterprise
         * and we use that value as a default one if no enterpriseId is provided.
         */
        const storedEnterpriseId = EnterpriseIdStorageService.get();

        currentScope = selectDefaultScope(commanderScopes);

        if (!currentScope) {
            return Promise.reject(new Error('No suitable scope found'));
        }

        ({ enterpriseId: currentEnterpriseId, profileId: currentProfileId } = currentScope);

        /**
         * In case we matched platform scope with accessAllEnterprises privilege and we also have storedEnterpriseId,
         * we should open the stored enterprise instead of the Mews Platform.
         */
        if (isPlatformAdmin(currentScope) && doesEnterpriseExist(storedEnterpriseId)) {
            currentEnterpriseId = storedEnterpriseId;
        }
    } else {
        currentScope =
            commanderScopes.find((s) => s.enterpriseId === currentEnterpriseId) ??
            commanderScopes.find(isPlatformAdmin);

        if (!currentScope) {
            /**
             *  If the enterpriseId in URL is invalid, we try to find a default scope
             */
            const defaultScope = selectDefaultScope(commanderScopes);

            if (defaultScope?.enterpriseId) {
                void navigate({
                    to: '/$currentEnterpriseId/Dashboard/Index',
                    params: { currentEnterpriseId: defaultScope.enterpriseId },
                });
            }

            return Promise.reject(new Error('Invalid enterpriseId'));
        }

        ({ profileId: currentProfileId } = currentScope);
    }

    return {
        currentProfileId,
        currentScope,
        currentEnterpriseId,
    };
};

export const selectDefaultScope = (scopes: GeneralApplicationScope[]) => {
    const storedEnterpriseId = EnterpriseIdStorageService.get();

    return (
        (storedEnterpriseId && scopes.find((s) => s.enterpriseId === storedEnterpriseId)) ??
        scopes.find((s) => s.isDefault) ??
        scopes.find(isPlatformAdmin) ??
        head(scopes)
    );
};

const setUserPrivileges = ({
    currentEnterpriseId,
    currentScope,
}: {
    currentEnterpriseId?: string | null;
    currentScope: GeneralApplicationScope;
}) => {
    const isInPlatform = currentEnterpriseId === null;

    if (isInPlatform) {
        /**
         * We set platform privileges only if you're actually in platform. That's because in many places, privileges checks are incorrectly
         * used to determine visibility of platform actions.
         *
         * The Platform is identified by having `null` as enterpriseId.
         */
        const privileges = currentScope.privileges as GeneralPlatformPrivileges;
        setPrivileges(createPrivilegesMap({}, privileges.accessAllEnterprises));
        setPlatformPrivileges(privileges);
    } else if (currentScope.type === ScopeType.Platform) {
        /**
         * In case the current Enterprise is accessed by a platform profile, we need to set all of the Enterprise privileges
         * based on `accessAllEnterprises` value.
         *
         * However, we don't set the platform privileges as we're not in Platform.
         */
        const privileges = currentScope.privileges as GeneralPlatformPrivileges;
        setPrivileges(createPrivilegesMap({}, privileges.accessAllEnterprises));
    } else {
        const privileges = currentScope.privileges as GeneralEnterprisePrivileges;
        setPrivileges(privileges);
    }
};

export const tryAutomaticSignIn = (): CommanderThunkAction<Promise<unknown>> => async (dispatch) => {
    /**
     * We clear existing cookies to ensure a consistent state of the app.
     */
    removeCookie('ProfileId');
    removeCookie('EnterpriseId');

    /**
     * We try to always silently sign in user on background, and swallow the error if we fail.
     * A future improvement should use a token created only for this specific purpose, instead of
     * an information-bearing cookie.
     */
    try {
        return dispatch(generalSignInPromise());
    } catch {
        return Promise.reject();
    }
};

export function generalSignInPromise(): CommanderThunkAction<Promise<unknown>> {
    return async (dispatch, _, { repositories }) =>
        repositories.generalUsers
            .signIn({
                ...INITIAL_SIGN_IN_PARAMS,
                device: getDeviceFingerprintPayload(),
                method: GeneralSignInMethod.AccessToken,
                application: GeneralArea.Commander,
            })
            .then(async (data) => dispatch(initializeUserSession(data)));
}

export const authenticateWithSecondFactor =
    ({
        pin,
        signInToken,
        redirectToDashboard = true,
        runPostSignInChecks,
    }: {
        pin: string;
        signInToken?: string;
        redirectToDashboard?: boolean;
        runPostSignInChecks?: boolean;
    }): CommanderThunkAction<Promise<void>> =>
    async (dispatch, getState, { repositories, navigate, featureFlagService }) => {
        const createPasskeyPageEnabled = () => {
            const passkeysKillSwitchEnabled = featureFlagService.hasFlag(FeatureFlag.passkeySignInUiEnabled);
            const hasPasskeySupport = browserSupportsWebAuthn();

            return passkeysKillSwitchEnabled && hasPasskeySupport;
        };

        return repositories.generalUsers
            .signIn({
                ...INITIAL_SIGN_IN_PARAMS,
                device: getDeviceFingerprintPayload(),
                pin,
                signInToken,
                method: isCurrentSessionLoggedIn() ? GeneralSignInMethod.AccessToken : GeneralSignInMethod.SignInToken,
                application: GeneralArea.Commander,
            })
            .then(async (data: GeneralUserAccessResult) => dispatch(checkForConsent(data)))
            .then(async (data) => dispatch(checkForHardDeviceApproval(data)))
            .then(async (data: GeneralUserAccessResult) => dispatch(initializeUserSession(data)))
            .then(async (data) => {
                if (runPostSignInChecks) {
                    if (
                        featureFlagService.hasFlag(FeatureFlag.passwordScannerEnabled) &&
                        data.passwordStrength === GeneralPasswordStrength.Weak
                    ) {
                        return navigate({
                            to: '/Home/WeakPassword',
                        });
                    }

                    if (createPasskeyPageEnabled() && data.passkeyInfo?.suggestSetup) {
                        return navigate({
                            to: '/Home/CreatePasskey',
                        });
                    }
                }

                if (redirectToDashboard) {
                    void navigate({
                        to: '/$currentEnterpriseId/Dashboard/Index',
                        params: { currentEnterpriseId: getStrictCurrentEnterpriseId() },
                    });
                }
            });
    };

export function getDeviceFingerprintPayload() {
    return { system: GeneralDeviceSystem.Web, id: getBrowserFingerprint() };
}
